import React, {useState, useEffect, useContext} from 'react';
import {Calendar, momentLocalizer, NavigateAction, View} from 'react-big-calendar'
import 'react-big-calendar/lib/css/react-big-calendar.css';
import moment from 'moment';
import {DataContext} from "../../contexts/dataContext";
import {translateService, Translate} from "../../services/TranslateService";
import { CircularProgress } from '@mui/material';
import { useValidEmail } from "../../hooks/useCustomerHooks";
import {cx} from "../MakeReservation/util";
import styles from "../MakeReservation/main.module.scss";
import {CustomerTextInput} from "../CustomerInput/CustomerInput";
import {useSelector} from "react-redux";
import {GlobalStore} from "../../reduxStore";
import reservedTableStyles from "./reservedTable.module.scss";

const localizer = momentLocalizer(moment);

interface ReservedSlot {
    from: string;
    to: string;
}

interface CalendarProps {
    isVisible: boolean;
    reservedIntervals: ReservedSlot[];
    vehicleId: string;
    reservedMessages: any;
}

const CalendarComponent = ({isVisible, reservedIntervals, vehicleId, reservedMessages}: CalendarProps) => {
    const [events, setEvents] = useState([]);
    const dataContext = useContext(DataContext);
    const currentDate = moment(dataContext.searchParameters?.endDate).toDate();
    const maxDate = moment(currentDate).add(8, 'weeks').toDate();
    const [isLoaded, setIsLoaded] = useState(true);
    const [currentlyPreBooked, setCurrentlyPreBooked] = useState('');
    const {email, setEmail, emailErrorString, email1FieldRef } = useValidEmail("", "emailField1", "", '');
    const [emailNotificationAlertHandled, setEmailNotificationAlertHandled] = useState(false);
    const [emailNotificationAlertFailed, setEmailNotificationAlertFailed] = useState('');
    const apiService = useSelector((state: GlobalStore) => state.chauffeurReducer.apiService);

    useEffect(() => {
        if (!isLoaded || !reservedIntervals || !isVisible) return;
        setIsLoaded(true);
        if(reservedIntervals !== undefined){
            let formattedEvents: any[] = [];

            if (reservedIntervals.length === 0) {
                // use BE message, otherwise show generic message
                setCurrentlyPreBooked(reservedMessages && reservedMessages.length ?
                    reservedMessages : translateService.translate('vehicleSearch1.vehiclePreReserved'));
                return;
            }

            reservedIntervals.forEach((reservedInterval: ReservedSlot, i: number) => {
                if(dataContext.searchParameters?.startDate){
                    formattedEvents.push({
                        title: <Translate content={'vehicleSearch1.Reserved'} />,
                        start: moment(reservedInterval.from).toDate(),
                        end: moment(reservedInterval.to).toDate(),
                        free: true
                    });
                }
            });

            if (events !== formattedEvents) {
                // @ts-ignore
                setEvents(formattedEvents);
            }
        }
    }, [dataContext.searchParameters?.startDate, dataContext.searchParameters?.endDate, reservedIntervals, isLoaded]);

    useEffect(() => {
        if(events.length !== 0){
            setIsLoaded(false);
            setCurrentlyPreBooked('');
        }
    }, [events]);

    const sendEmailForUpdates = () => {
        apiService?.user.askNotificationsForAlerts(vehicleId,
            dataContext.searchParameters?.startDate?.format('DD.MM.YYYY') + ' '
            + dataContext.searchParameters?.startDate?.format('HH:mm'),
            dataContext.searchParameters?.endDate?.format('DD.MM.YYYY')
            + ' ' + dataContext.searchParameters?.endDate?.format('HH:mm'), email,
            dataContext.searchParameters?.pickupCity + ' - ' +
            dataContext.searchParameters?.returnCity)
            .then(() => {
                setEmailNotificationAlertHandled(true);
            }).catch(() => {
                setEmailNotificationAlertFailed(translateService.translate('vehicleSearch1.emailAlertFailed'));
            })
    }

    const handleNavigate = (newDate: Date, view: View, action: NavigateAction) => {
        const prevButton = document.querySelector('.rbc-btn-group > button:nth-child(2)');
        const nextButton = document.querySelector('.rbc-btn-group > button:nth-child(3)');
        if (action === 'NEXT' && moment(newDate).isAfter(maxDate) && nextButton) {
            nextButton.setAttribute('disabled', 'true');
        } else if (action === 'PREV' && moment(newDate).isBefore(dataContext.searchParameters?.startDate) && prevButton) {
            prevButton.setAttribute('disabled', 'true');
        } else {
            if (nextButton) {
                nextButton.removeAttribute('disabled');
            }
            if (prevButton) {
                prevButton.removeAttribute('disabled');
            }
        }
    }

    // @ts-ignore
    return (
        <div>
            {isLoaded ?
                currentlyPreBooked === '' ?
                    <div className={reservedTableStyles.spinnerContainer}>
                        <CircularProgress color={"warning"}/>
                    </div>
                    : <div className={reservedTableStyles.prebookedContainer}>
                        { dataContext.oneWayRes ?
                                <>
                                    <p>{translateService.translate('vehicleSearch1.onewayDescription')}</p>
                                    <p>{translateService.translate('vehicleSearch1.onewayReservedEmptyEmail')}</p>
                                    <div className={reservedTableStyles.emailInputContainer}>
                                        <CustomerTextInput placeholder={translateService.translate("vehicleSearch1.email") + " *"}
                                                           errorString={emailErrorString} value={email} formSubmitted={true}
                                                           fieldName={email1FieldRef} setValue={setEmail}
                                                           className={cx(styles.r24ChildFlex1, styles.grayBorderAround,
                                                               styles.vehicleSearch)}
                                                           disableInput={emailNotificationAlertHandled} />
                                    </div>
                                    { emailNotificationAlertHandled ?
                                            <div className="text-center">
                                                <Translate content={'vehicleSearch1.emailAlertSent'}
                                                           className={reservedTableStyles.alertSent}/>
                                            </div> :
                                        <div className={reservedTableStyles.alertFailedContainer}>
                                            <button className={cx("btn btn-all mt-1 w-50", reservedTableStyles.sendAlertBtn)}
                                                    disabled={emailErrorString !== ''}
                                                    onClick={()=> {
                                                        if (emailNotificationAlertFailed !== '') {
                                                            // trying to send alert again, remove the error message
                                                            setEmailNotificationAlertFailed('');
                                                        }
                                                        sendEmailForUpdates()}}>
                                                {translateService.translate('vehicleSearch1.sendAlert')}
                                            </button>
                                            <span className={reservedTableStyles.alertErrorText}>
                                                { emailNotificationAlertFailed }
                                            </span>
                                        </div>
                                    } </> : <p >{currentlyPreBooked}</p>
                        }
                    </div>
                : <Calendar
                    allDayAccessor=""
                    localizer={localizer}
                    events={events} // events is an array of objects
                    startAccessor="start"
                    endAccessor="end"
                    views={['week']}
                    messages={{
                        next: '>',
                        previous: '<',
                        allDay: '',
                        today: `${translateService.translate('vehicleSearch1.today')}`
                    }}
                    showMultiDayTimes={true}
                    defaultView="week"
                    selectable={true}
                    style={{height: 500}}
                    onNavigate={handleNavigate}
                />}
        </div>
    );
};

export default CalendarComponent;